import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import {
    ChevronLeft, Save, Loader2, Plus, Trash2,
    Settings2, Palette, Eye, Code, Copy, X, Undo2, Redo2, Monitor, Tablet, Smartphone,
    Columns, Minus, Info,
} from 'lucide-react';
// Canvas drag-and-drop is a custom POINTER-EVENT engine (see the WYSIWYG
// Canvas section) — native HTML5 DnD proved unreliable across environments.

import { useUndoRedo } from '../hooks/useUndoRedo';
import { useAutoSave } from '../hooks/useAutoSave';
import { useUnsavedChanges } from '../hooks/useUnsavedChanges';
import FormTemplateGallery from '../components/FormTemplateGallery';
import FieldInspector from '../components/forms/FieldInspector';
import { NurturePhoneInput } from '../components/ui/phone-input';
import { objectToE164 } from '../lib/phoneUtils';
import DOMPurify from 'dompurify';
import { buildFormEmbedSnippet } from '../components/forms/embedSnippet';
import MultiSelectDropdown from '../components/forms/MultiSelectDropdown';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

import { FIELD_TYPES, TYPE_ICONS, INPUT, LABEL, marginStyle, paddingStyle } from '../lib/formConstants';
import ColorField from '../components/ColorField';
import { DEVICE_WIDTHS, DEVICE_VALUES, DEVICE_LABELS } from '../lib/deviceConstants';
import { evaluateShowWhen } from '../lib/fieldConditions';
import { flattenFields } from '../lib/formUtils';
import { errorText } from '../lib/apiErrors';

// Font options for per-step font styling (web-safe + Google Fonts)
const FONT_OPTIONS = [
    // Web-safe system fonts (no loading needed)
    { value: 'Arial, sans-serif',           label: 'Arial' },
    { value: 'Helvetica, sans-serif',        label: 'Helvetica' },
    { value: 'Times New Roman, serif',       label: 'Times New Roman' },
    { value: 'Georgia, serif',               label: 'Georgia' },
    { value: 'Courier New, monospace',       label: 'Courier New' },
    { value: 'Verdana, sans-serif',          label: 'Verdana' },
    { value: 'Tahoma, sans-serif',           label: 'Tahoma' },
    { value: 'Trebuchet MS, sans-serif',     label: 'Trebuchet MS' },
    { value: 'Impact, sans-serif',           label: 'Impact' },
    { value: 'Comic Sans MS, cursive',       label: 'Comic Sans MS' },
    // Google Fonts (loaded via <link> in index.html)
    { value: 'Inter, sans-serif',            label: 'Inter' },
    { value: 'Manrope, sans-serif',          label: 'Manrope' },
    { value: 'Roboto, sans-serif',            label: 'Roboto' },
    { value: 'Open Sans, sans-serif',        label: 'Open Sans' },
    { value: 'Lato, sans-serif',             label: 'Lato' },
    { value: 'Montserrat, sans-serif',       label: 'Montserrat' },
    { value: 'Poppins, sans-serif',          label: 'Poppins' },
    { value: 'Raleway, sans-serif',          label: 'Raleway' },
    { value: 'Nunito, sans-serif',           label: 'Nunito' },
    { value: 'Playfair Display, serif',      label: 'Playfair Display' },
    { value: 'Merriweather, serif',          label: 'Merriweather' },
    { value: 'Source Sans Pro, sans-serif',  label: 'Source Sans Pro' },
];

function genId() { return 'fld_' + Math.random().toString(36).substr(2, 9); }

// ─── Tree helpers ──────────────────────────────────────────────────────────

function cloneFieldWithNewIds(node) {
    const clone = { ...node, field_id: genId() };
    if (node.field_type === 'container' && node.children) {
        clone.children = node.children.map(cloneFieldWithNewIds);
    }
    return clone;
}

function findNode(nodes, fieldId, parent = null) {
    for (const node of nodes) {
        if (node.field_id === fieldId) return { node, parent };
        if (node.field_type === 'container' && node.children) {
            const found = findNode(node.children, fieldId, node);
            if (found) return found;
        }
    }
    return null;
}

function removeNode(nodes, fieldId) {
    return nodes
        .filter(n => n.field_id !== fieldId)
        .map(n => {
            // Clear any mirror_source_field_id references to the deleted field
            if (n.config?.mirror_source_field_id === fieldId) {
                const { mirror_source_field_id, ...restConfig } = n.config;
                return { ...n, config: restConfig };
            }
            if (n.field_type === 'container' && n.children) {
                return { ...n, children: removeNode(n.children, fieldId) };
            }
            return n;
        });
}

function updateNode(nodes, fieldId, updates) {
    return nodes.map(n => {
        if (n.field_id === fieldId) return { ...n, ...updates };
        if (n.field_type === 'container' && n.children) {
            return { ...n, children: updateNode(n.children, fieldId, updates) };
        }
        return n;
    });
}

function insertNode(nodes, newNode, targetIndex, parentId = null) {
    if (parentId === null) {
        const next = [...nodes];
        next.splice(targetIndex, 0, newNode);
        return next;
    }
    return nodes.map(n => {
        if (n.field_id === parentId && n.field_type === 'container') {
            const children = [...(n.children || [])];
            children.splice(targetIndex, 0, newNode);
            return { ...n, children };
        }
        if (n.field_type === 'container' && n.children) {
            return { ...n, children: insertNode(n.children, newNode, targetIndex, parentId) };
        }
        return n;
    });
}

function moveNode(nodes, sourceId, targetParentId, targetIndex) {
    const found = findNode(nodes, sourceId);
    if (!found) return nodes;
    const nodeToMove = found.node;
    let tree = removeNode(nodes, sourceId);
    tree = insertNode(tree, nodeToMove, targetIndex, targetParentId);
    return tree;
}

function countFields(nodes) {
    let count = 0;
    for (const n of nodes) {
        count++;
        if (n.field_type === 'container' && n.children) {
            count += countFields(n.children);
        }
    }
    return count;
}


function isDescendant(node, targetId) {
    if (node.field_id === targetId) return true;
    if (node.children) {
        return node.children.some(c => isDescendant(c, targetId));
    }
    return false;
}

// shouldShow was replaced by the shared evaluateShowWhen from lib/fieldConditions.
// Call sites in CanvasElement receive fieldsById via canvasCtx.

// ─── Tag Input ─────────────────────────────────────────────────────────────

function TagInput({ value = [], onChange, placeholder = 'Type and press Enter...' }) {
    const [input, setInput] = useState('');
    const add = () => {
        const v = input.trim();
        if (v && !value.includes(v)) { onChange([...value, v]); }
        setInput('');
    };
    return (
        <div>
            <div className="flex flex-wrap gap-1 mb-1">
                {value.map((t, i) => (
                    <span key={i} className="inline-flex items-center gap-1 bg-stone-100 text-stone-700 text-xs px-2 py-0.5 rounded-full">
                        {t}
                        <button onClick={() => onChange(value.filter((_, j) => j !== i))} className="text-stone-400 hover:text-red-500">
                            <X className="w-3 h-3" />
                        </button>
                    </span>
                ))}
            </div>
            <input value={input} onChange={e => setInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
                placeholder={placeholder} className={INPUT} />
        </div>
    );
}

// ─── WYSIWYG Canvas — the interactive form IS the editor ────────────────────
// Every element renders exactly as the public form renders it (live inputs),
// wrapped in a selection shell. The whole element body is the drag handle —
// only the actual controls (inputs, selects, buttons) are excluded so they
// stay usable.
//
// Drag engine: POINTER EVENTS ONLY — no native HTML5 drag-and-drop. Native
// DnD proved unreliable across environments (browser shields/extensions can
// skew drag-event coordinates, and Chrome aborts drags on DOM churn), so the
// canvas listens to pointerdown/pointermove/pointerup and hit-tests drop
// targets itself via elementFromPoint. A drag arms on pointerdown and
// activates after ~6px of movement (a plain click still selects). While
// dragging, a cursor ghost follows the pointer; entering a drop zone hides
// it and renders the ghost IN the form as a dashed placeholder at the
// prospective position. The source element collapses in place and the real
// `fields` state changes only on release. Esc cancels.

/** Live input rendering for a non-container node (same markup as the public form). */
function FieldInputPreview({ node, answers, setAnswers, error }) {
    const answerKey = node.field_id;

    if (node.field_type === 'section_heading')
        return <h3 className="text-sm font-semibold text-stone-700 border-b border-stone-200 pb-1">{node.label || 'Section Heading'}</h3>;
    if (node.field_type === 'divider')
        return <hr className="border-stone-300" />;
    if (node.field_type === 'spacer')
        return (
            <div style={{ height: `${node.spacer_height || 32}px` }} className="flex items-center justify-center">
                <span className="text-[10px] text-stone-300 flex items-center gap-1">
                    <Minus className="w-3 h-3" /> Spacer · {node.spacer_height || 32}px
                </span>
            </div>
        );
    if (node.field_type === 'html_block')
        return <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(node.html_content || '') }} />;

    const inputType = node.field_type === 'email' ? 'email' : node.field_type === 'number' ? 'number' : node.field_type === 'date' ? 'date' : node.field_type === 'url' ? 'url' : 'text';

    return (
        <div>
            {node.field_type !== 'checkbox' && (
                <label className="block text-sm font-medium text-stone-700 mb-1">
                    {node.label || 'Untitled'} {node.required && <span className="text-red-500">*</span>}
                </label>
            )}
            {node.help_text && <p className="text-xs text-stone-400 mb-1">{node.help_text}</p>}
            {node.field_type === 'textarea' ? (
                <textarea value={answers[answerKey] || ''} onChange={e => setAnswers(prev => ({ ...prev, [answerKey]: e.target.value }))}
                    rows={3} className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white" placeholder={node.placeholder} />
            ) : node.field_type === 'select' ? (
                (() => {
                    const mirrorSourceId = node.config?.mirror_source_field_id;
                    let effectiveOptions = (node.options || []).filter(Boolean);
                    let mirrorDisabled = false;

                    if (mirrorSourceId) {
                        const sourceAnswer = answers[mirrorSourceId];
                        const sourceSelections = Array.isArray(sourceAnswer) ? sourceAnswer : [];
                        effectiveOptions = sourceSelections.filter(Boolean);
                        mirrorDisabled = effectiveOptions.length === 0;
                    }

                    return (
                        <select value={answers[answerKey] || ''} disabled={mirrorDisabled}
                            onChange={e => setAnswers(prev => ({ ...prev, [answerKey]: e.target.value }))}
                            className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white">
                            <option value="">{mirrorDisabled ? 'Select options above first…' : 'Select...'}</option>
                            {effectiveOptions.map(o => <option key={o} value={o}>{o}</option>)}
                        </select>
                    );
                })()
            ) : node.field_type === 'multi_select' ? (
                <MultiSelectDropdown
                    options={node.options || []}
                    value={answers[answerKey] || []}
                    onChange={val => setAnswers(prev => ({ ...prev, [answerKey]: val }))}
                    placeholder={node.placeholder || 'Select…'}
                    className={INPUT}
                    maxSelections={node.config?.max_selections}
                />
            ) : node.field_type === 'checkbox' ? (
                <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={!!answers[answerKey]}
                        onChange={e => setAnswers(prev => ({ ...prev, [answerKey]: e.target.checked }))}
                        className="rounded border-stone-300" />
                    {node.placeholder || node.label}
                </label>
            ) : node.field_type === 'phone' ? (
                <NurturePhoneInput value={objectToE164(answers[answerKey])} onChange={(e164) => setAnswers(prev => ({ ...prev, [answerKey]: e164 || '' }))} placeholder={node.placeholder} className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white" aria-label={node.label} />
            ) : (
                <input type={inputType} value={answers[answerKey] || ''}
                    onChange={e => setAnswers(prev => ({ ...prev, [answerKey]: e.target.value }))}
                    className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white" placeholder={node.placeholder} />
            )}
            {error && <p className="text-xs text-red-500 mt-0.5">{error}</p>}
        </div>
    );
}

/** GhostBody — non-interactive rendering of a node for the two ghosts
    (cursor-following and in-form placeholder). */
function GhostBody({ node }) {
    if (node.field_type === 'container') {
        const children = node.children || [];
        return (
            <div className="flex items-center gap-2 text-xs text-stone-500">
                <Columns className="w-3.5 h-3.5 text-indigo-300" />
                {node.label || 'Container'}
                <span className="fbw-badge">{node.columns || 1} col · {children.length}</span>
            </div>
        );
    }
    return <FieldInputPreview node={node} answers={{}} setAnswers={() => {}} error={null} />;
}

/** The in-form drop placeholder — the ghost rendered at the drop position.
    Pointer-transparent; the drag engine geometrically keeps the target
    stable while the pointer is inside it. */
function CanvasGhost({ node }) {
    return (
        <div className="fbw-ghost" style={marginStyle(node)}>
            <GhostBody node={node} />
        </div>
    );
}

/** DropGap — slim "Drop here" strip shown between elements while a drag is
    active. The drag engine hit-tests it by its data attributes; hovering it
    swaps it for the ghost placeholder at this slot. */
function DropGap({ parentId, index }) {
    return (
        <div className="fbw-drop-gap" data-drop-gap data-parent-id={parentId || ''} data-index={index}>
            <span>Drop here</span>
        </div>
    );
}

/**
 * CanvasElement — one selectable/draggable element on the canvas.
 *
 * The wrapper carries data attributes (field id, parent, index, orientation)
 * that the pointer drag engine reads during elementFromPoint hit-testing:
 * before/after by vertical midpoint in single-column flow, by horizontal
 * midpoint inside multi-column containers (grid order is row-major, so
 * left = earlier index).
 */
function CanvasElement({ node, index, parentId, horizontal, ctx }) {
    const {
        selectedFieldId, onSelect, onDelete,
        dragId, onPointerDragStart, consumeClickSuppression,
        answers, setAnswers, errors, fieldsById,
    } = ctx;

    const isSelected = selectedFieldId === node.field_id;
    const isDragSource = dragId === node.field_id;
    const isHidden = !!node.hidden;
    const dimmed = isHidden || !evaluateShowWhen(node, answers, fieldsById);

    const handlePointerDown = (e) => {
        if (e.button !== 0) return;
        // Controls stay usable — a press on one never arms a drag.
        if (e.target.closest('input, textarea, select, button, a, [contenteditable="true"]')) return;
        e.stopPropagation();
        onPointerDragStart(node, e, e.currentTarget.getBoundingClientRect());
    };

    const handleClick = (e) => {
        e.stopPropagation();
        if (consumeClickSuppression()) return; // this "click" was a drag release
        onSelect(node.field_id);
    };

    let body;
    if (node.field_type === 'container') {
        const cols = node.columns || 1;
        const children = node.children || [];
        body = (
            <div>
                <div className="flex items-center gap-2 mb-1">
                    <Columns className="w-3.5 h-3.5 text-indigo-300 flex-shrink-0" />
                    <span className="text-xs text-stone-500 truncate">{node.label || 'Container'}</span>
                    <span className="fbw-badge flex-shrink-0">{cols} col · {children.length}</span>
                </div>
                <div
                    data-container-body
                    data-container-id={node.field_id}
                    data-child-count={children.length}
                    className={`fbw-container-body ${dragId && !isDragSource ? 'droppable' : ''}`}
                >
                    {children.length === 0 && !(ctx.dropTarget && ctx.dropTarget.parentId === node.field_id) ? (
                        <p className="text-[10px] text-stone-400 text-center py-4">Drag elements here, or select this container and use “Add Element”</p>
                    ) : (
                        <CanvasStack nodes={children} parentId={node.field_id} cols={cols} ctx={ctx} />
                    )}
                </div>
            </div>
        );
    } else {
        body = <FieldInputPreview node={node} answers={answers} setAnswers={setAnswers} error={errors[node.field_id]} />;
    }

    return (
        <div
            data-canvas-el
            data-field-id={node.field_id}
            data-parent-id={parentId || ''}
            data-index={index}
            data-horizontal={horizontal ? '1' : '0'}
            onPointerDown={handlePointerDown}
            onClick={handleClick}
            className={`fbw-el ${isSelected ? 'selected' : ''} ${isDragSource ? 'drag-source' : ''}`}
            style={marginStyle(node)}
        >
            <div className="fbw-tools" onClick={(e) => e.stopPropagation()}>
                <button className="fbw-del" title="Delete element" onClick={() => onDelete(node.field_id)}>
                    <Trash2 className="w-3 h-3" />
                </button>
            </div>
            {isHidden
                ? <span className="fbw-derived-badge">
                      Hidden{node.config?.derive?.source_field_id ? ' · derived' : ''}
                  </span>
                : dimmed && <span className="fbw-hidden-badge">Hidden by condition</span>}
            <div className={dimmed ? 'opacity-40' : ''}>{body}</div>
        </div>
    );
}

/**
 * CanvasStack — a list of elements: vertical flow at root, N-column grid
 * inside containers. Injects the ghost placeholder at the pending drop
 * position; existing element nodes are never reordered during a drag.
 */
function CanvasStack({ nodes, parentId, cols = 1, ctx }) {
    // Collapse columns based on the device toggle — phone/tablet always
    // get 1 column; desktop keeps the configured column count. The old
    // CSS @media rule was removed (it checked the browser viewport, not
    // the canvas card width, so "phone" on a desktop browser kept 2 cols
    // in a 375px canvas).
    const device = ctx.device || 'desktop';
    if (device === 'phone' || device === 'tablet') cols = 1;
    const horizontal = cols > 1;
    const { dropTarget, dragNode, dragId } = ctx;
    const ghostAt = dropTarget && dropTarget.parentId === parentId && dragNode ? dropTarget.index : -1;
    // "Drop here" strips between elements while dragging — vertical stacks
    // only (strips would occupy grid cells and break multi-column layouts;
    // element-half + container-body targeting covers those). Slots touching
    // the collapsed source are no-op drops, so no strip is shown there.
    const srcIdx = dragId ? nodes.findIndex(n => n.field_id === dragId) : -1;
    const showGapAt = (i) =>
        dragId && dragNode && !horizontal && ghostAt !== i &&
        !(srcIdx >= 0 && (i === srcIdx || i === srcIdx + 1));
    return (
        <div className={horizontal ? 'container-grid' : 'space-y-3'} style={horizontal ? { '--container-cols': cols } : undefined}>
            {nodes.map((node, i) => (
                <React.Fragment key={node.field_id}>
                    {ghostAt === i && <CanvasGhost node={dragNode} />}
                    {showGapAt(i) && <DropGap parentId={parentId} index={i} />}
                    <CanvasElement node={node} index={i} parentId={parentId} horizontal={horizontal} ctx={ctx} />
                </React.Fragment>
            ))}
            {ghostAt >= nodes.length && <CanvasGhost node={dragNode} />}
            {ghostAt < nodes.length && showGapAt(nodes.length) && nodes.length > 0 && (
                <DropGap parentId={parentId} index={nodes.length} />
            )}
        </div>
    );
}

// ─── Main FormBuilder Component ────────────────────────────────────────────

export default function FormBuilder() {
    const { formId } = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { getAuthHeader } = useAuth();
    const [form, setForm] = useState(null);
    const [loading, setLoading] = useState(!!formId);
    const [saving, setSaving] = useState(false);
    // `?tab=settings` (or styling / embed) opens that tab.
    const [activeTab, setActiveTab] = useState(() => {
        const tab = searchParams.get('tab');
        return ['form', 'settings', 'styling', 'embed'].includes(tab) ? tab : 'form';
    });
    const [customFields, setCustomFields] = useState([]);
    const [showTemplateGallery, setShowTemplateGallery] = useState(false);
    const [previewAnswers, setPreviewAnswers] = useState({});
    const [previewErrors, setPreviewErrors] = useState({});
    const { isDirty, setIsDirty, blockedNavigate, showNavConfirm, pendingNav, confirmLeave, cancelLeave } = useUnsavedChanges(navigate);
    const [hasManualSaved, setHasManualSaved] = useState(false);
    const [paneWidth, setPaneWidth] = useState(360);
    const [device, setDevice] = useState('desktop');
    const [embedDevice, setEmbedDevice] = useState('desktop');
    const [selectedFieldId, setSelectedFieldId] = useState(null);
    const [showQuickAdd, setShowQuickAdd] = useState(false);
    // Active drag on the canvas (pointer-event engine). `fields` is never
    // touched during the drag — a custom ghost follows the pointer, and
    // `dropTarget` {parentId, index} is where CanvasStack injects the
    // in-form ghost placeholder. The move commits (one undo entry) only on
    // release over the canvas. Refs mirror the state for the window-level
    // listeners, which are created at pointerdown before the state flips.
    const [dragId, setDragId] = useState(null);
    const [dragNode, setDragNode] = useState(null);
    const [dropTarget, setDropTargetState] = useState(null);
    const dragIdRef = useRef(null);
    const dragNodeRef = useRef(null);
    const dropTargetRef = useRef(null);
    const suppressClickRef = useRef(false);
    const dragSizeRef = useRef({ width: 320 });
    // Where inside the element the user grabbed it (clamped) — the cursor
    // ghost is anchored so the pointer holds it by its top-left corner area.
    const grabOffsetRef = useRef({ x: 16, y: 16 });
    const ghostPosRef = useRef({ x: 0, y: 0 });
    const cursorGhostRef = useRef(null);
    const [steps, setSteps] = useState([]);
    // Index into `steps` of the step this builder session edits (from the
    // ?step= param or the first question step); -1 = legacy top-level fields.
    const [activeStepIdx, setActiveStepIdx] = useState(-1);
    // Loaded and passed through on save so config authored on the dedicated
    // Forwarder/Redirect nodes is never wiped — not editable here.
    const [forwarders, setForwarders] = useState([]);
    const isNew = !formId || formId === 'new';

    const { state: fields, set: setFields, undo, redo, canUndo, canRedo } = useUndoRedo([]);

    // Live mirror of `fields` for the drag engine's window-level listeners.
    const fieldsRef = useRef([]);
    useEffect(() => { fieldsRef.current = fields; }, [fields]);

    // Fetch custom field definitions
    useEffect(() => {
        axios.get(`${API}/admin/custom-fields?entity=contact`, { headers: getAuthHeader() })
            .then(r => setCustomFields(Array.isArray(r.data) ? r.data : (r.data?.data || [])))
            .catch(() => setCustomFields([]));
    }, [getAuthHeader]);

    // Load form or show template gallery for new forms
    useEffect(() => {
        if (isNew) {
            setShowTemplateGallery(true);
            setForm({
                name: 'Untitled Form', description: '',
                fields: [],
                settings: {
                    confirmation_message: 'Thank you for your submission!',
                    notify_emails: [],
                },
                styling: { brand_color: '#1c1917', button_text: 'Submit', show_branding: true, background: 'transparent', border_radius: 12, border_color: '#E8E5DF', border_width: 1, button_color: null, stepper_completed_color: null, stepper_active_color: null, stepper_upcoming_color: null, padding: { top: 0, right: 0, bottom: 0, left: 0 } },
            });
            setLoading(false);
            return;
        }
        axios.get(`${API}/forms/${formId}`, { headers: getAuthHeader() })
            .then(res => {
                setForm(res.data);
                // Auto-migrate: copy form-level styling to any question step that
                // doesn't have its own step-level styling. This ensures backward
                // compatibility — existing forms get per-step styling from the
                // form-level defaults, new forms get fresh per-step styling.
                const formStyling = res.data.styling || {};
                const migratedSteps = (res.data.steps || []).map(s =>
                    (s.kind === 'question' && !s.styling)
                        ? { ...s, styling: { ...formStyling } }
                        : s
                );
                setSteps(migratedSteps);
                if (res.data.forwarders) setForwarders(res.data.forwarders);
                // Resolve which step this builder session edits: the ?step= param
                // (set by the workflow canvas's Edit Node), else the first
                // question step. Steps are structured on the workflow canvas;
                // the builder only edits one step's fields.
                const stepParam = searchParams.get('step');
                let idx = stepParam ? migratedSteps.findIndex(s => s.step_id === stepParam) : -1;
                if (idx < 0) idx = migratedSteps.findIndex(s => s.kind === 'question');
                setActiveStepIdx(idx);
                const stepFields = idx >= 0 ? (migratedSteps[idx].fields || []) : [];
                const firstQuestionIdx = migratedSteps.findIndex(s => s.kind === 'question');
                // Legacy heal: forms saved before fields were written into steps
                // carry their fields at the top level only — surface those when
                // the target step is the first question step and still empty.
                if (stepFields.length === 0 && (idx < 0 || idx === firstQuestionIdx) && (res.data.fields || []).length > 0) {
                    setFields(res.data.fields);
                } else {
                    setFields(stepFields);
                }
            })
            .catch(() => { toast.error('Form not found'); navigate('/forms'); })
            .finally(() => setLoading(false));
        // Only re-fetch when the form changes. getAuthHeader is recreated by
        // AuthContext on every token refresh; having it here re-ran this
        // effect and silently reverted unsaved edits (and now an unsaved event
        // pick) to the saved copy. getAuthHeader falls back to localStorage, so
        // the closure is safe.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formId, isNew, navigate, setFields]);

    useEffect(() => { setIsDirty(true); }, [fields, setIsDirty]);

    // beforeunload guard and in-app navigation guard are provided by
    // useUnsavedChanges (see hook for details).

    /**
     * Build the PUT payload from the edited fields. The edited fields are
     * written into the step this session targets (so the workflow canvas and
     * the multi-step renderer see them) and mirrored to the top-level
     * `fields` when the target is the first question step — single-page
     * consumers read `form.fields` first.
     */
    const buildSavePayload = (fieldsData) => {
        const nextSteps = (activeStepIdx >= 0 && steps[activeStepIdx])
            ? steps.map((s, i) => (i === activeStepIdx ? { ...s, fields: fieldsData } : s))
            : steps;
        const firstQuestionIdx = steps.findIndex(s => s.kind === 'question');
        const mirrorTopLevel = activeStepIdx < 0 || activeStepIdx === firstQuestionIdx;
        // Event registration is set in the workflow canvas's Form Settings (#919 QA round 2), so the page editor never
        // writes it: the server keeps the stored link for an omitted key (C22). Sending the copy loaded with this page
        // would put back an event the canvas changed or switched off since, on the next autosave.
        const { event_registration: _registration, ...settings } = form.settings || {};
        return {
            ...form,
            settings,
            fields: mirrorTopLevel ? fieldsData : (form.fields || []),
            steps: nextSteps,
            forwarders,
            status: form.status || 'draft',
        };
    };

    const { status: saveStatus } = useAutoSave(
        isDirty && hasManualSaved ? fields : null,
        async (data) => {
            if (isNew || !formId || formId === 'new') return;
            const res = await axios.put(`${API}/forms/${formId}`, buildSavePayload(data), { headers: getAuthHeader() });
            if (res.data.steps) setSteps(res.data.steps);
            setIsDirty(false);
        },
        2000,
        hasManualSaved
    );

    // blockedNavigate is provided by useUnsavedChanges above.

    const handleManualSave = async () => {
        if (!form?.name?.trim()) { toast.error('Form name required'); return; }
        setSaving(true);
        try {
            const payload = buildSavePayload(fields);
            if (isNew) {
                const res = await axios.post(`${API}/forms`, payload, { headers: getAuthHeader() });
                toast.success('Form created');
                navigate(`/forms/${res.data.form_id}/workflow`, { replace: true });
                setForm(res.data);
            } else {
                const res = await axios.put(`${API}/forms/${formId}`, payload, { headers: getAuthHeader() });
                toast.success('Form saved');
                setForm(res.data);
                if (res.data.steps) setSteps(res.data.steps);
            }
            setIsDirty(false);
            setHasManualSaved(true);
        } catch (err) { toast.error(errorText(err, 'Failed to save')); }
        finally { setSaving(false); }
    };

    // ─── Pointer-event drag engine ───────────────────────────────────────
    // No native HTML5 DnD. A drag arms on pointerdown, activates after ~6px
    // of movement, tracks via window pointermove (coordinates come straight
    // from the pointer — nothing can skew them), hit-tests drop targets with
    // elementFromPoint, and commits on pointerup. Esc cancels. `fields` is
    // never touched during the drag.

    /** setDropAt — point the ghost placeholder at (parentId, index), or null
        to clear. Guarded against no-op updates and against a container
        targeting itself or its own descendant. */
    const setDropAt = useCallback((target) => {
        const cur = dropTargetRef.current;
        if (!target) {
            if (cur) { dropTargetRef.current = null; setDropTargetState(null); }
            return;
        }
        if (cur && cur.parentId === target.parentId && cur.index === target.index) return;
        const node = dragNodeRef.current;
        if (node && node.field_type === 'container' && target.parentId &&
            (target.parentId === node.field_id || isDescendant(node, target.parentId))) return;
        dropTargetRef.current = target;
        setDropTargetState(target);
    }, []);

    /** computeTarget — hit-test the drop position under the pointer. */
    const computeTarget = useCallback((x, y) => {
        // Stability: while the pointer is inside the in-form ghost, keep the
        // current target (the ghost occupies the slot it points at).
        const ghostEl = document.querySelector('.fb-canvas-card .fbw-ghost');
        if (ghostEl) {
            const r = ghostEl.getBoundingClientRect();
            if (x >= r.left && x <= r.right && y >= r.top && y <= r.bottom) return dropTargetRef.current;
        }
        const under = document.elementFromPoint(x, y);
        if (!under || !under.closest || !under.closest('.fb-canvas-card')) return null;
        const gap = under.closest('[data-drop-gap]');
        if (gap) return { parentId: gap.dataset.parentId || null, index: Number(gap.dataset.index) };
        const wrap = under.closest('[data-canvas-el]');
        if (wrap) {
            if (wrap.dataset.fieldId === dragIdRef.current) return dropTargetRef.current;
            // Inside a container's body but not over one of its children →
            // append at the container's end (empty body or padding).
            const bodyIn = under.closest('[data-container-body]');
            if (bodyIn && wrap.contains(bodyIn) && bodyIn.dataset.containerId !== dragIdRef.current) {
                return { parentId: bodyIn.dataset.containerId, index: Number(bodyIn.dataset.childCount) };
            }
            const r = wrap.getBoundingClientRect();
            const after = wrap.dataset.horizontal === '1'
                ? x > r.left + r.width / 2
                : y > r.top + r.height / 2;
            return { parentId: wrap.dataset.parentId || null, index: Number(wrap.dataset.index) + (after ? 1 : 0) };
        }
        const body = under.closest('[data-container-body]');
        if (body && body.dataset.containerId !== dragIdRef.current) {
            return { parentId: body.dataset.containerId, index: Number(body.dataset.childCount) };
        }
        // Card padding / title / buttons → append at root end.
        return { parentId: null, index: fieldsRef.current.length };
    }, []);

    /** commitDrop — apply the pending move to the real fields state.
        Reads everything from refs: it runs inside listeners created at
        pointerdown, before the drag state ever flipped. */
    const commitDrop = useCallback(() => {
        const id = dragIdRef.current;
        const target = dropTargetRef.current;
        if (id && target) {
            let { parentId, index } = target;
            const base = fieldsRef.current;
            // moveNode removes the source before inserting — when moving down
            // within the same parent, the removal shifts the target one slot left.
            const siblings = parentId ? (findNode(base, parentId)?.node?.children || []) : base;
            const srcIdx = siblings.findIndex(n => n.field_id === id);
            if (srcIdx >= 0 && srcIdx < index) index -= 1;
            if (!(srcIdx >= 0 && index === srcIdx)) {
                setFields(prev => moveNode(prev, id, parentId, index));
            }
        }
        dragIdRef.current = null;
        dragNodeRef.current = null;
        dropTargetRef.current = null;
        setDropTargetState(null);
        setDragNode(null);
        setDragId(null);
    }, [setFields]);

    /** startPointerDrag — arm a drag from an element's pointerdown. */
    const startPointerDrag = useCallback((node, e, rect) => {
        e.preventDefault(); // stop text selection from starting under the press
        const startX = e.clientX, startY = e.clientY;
        let active = false;
        let scroller = null;

        const onMove = (ev) => {
            if (!active) {
                if (Math.hypot(ev.clientX - startX, ev.clientY - startY) < 6) return;
                active = true;
                suppressClickRef.current = true;
                scroller = document.querySelector('.fb-tab-content');
                dragSizeRef.current = { width: Math.round(rect.width) };
                // The cursor holds the ghost card by its top-left corner area
                // no matter where inside the element the grab happened.
                grabOffsetRef.current = {
                    x: Math.max(12, Math.min(startX - rect.left, 48)),
                    y: Math.max(10, Math.min(startY - rect.top, 18)),
                };
                ghostPosRef.current = { x: ev.clientX, y: ev.clientY };
                dragIdRef.current = node.field_id;
                dragNodeRef.current = node;
                document.body.classList.add('fbw-dragging');
                setDragNode(node);
                setDragId(node.field_id);
            }
            ev.preventDefault();
            ghostPosRef.current = { x: ev.clientX, y: ev.clientY };
            // Auto-scroll the canvas scrollport near its edges.
            if (scroller) {
                const sr = scroller.getBoundingClientRect();
                if (ev.clientY < sr.top + 48) scroller.scrollTop -= 12;
                else if (ev.clientY > sr.bottom - 48) scroller.scrollTop += 12;
            }
            setDropAt(computeTarget(ev.clientX, ev.clientY));
            // Position the ghost for its current mode: small chip beside the
            // pointer inside a drop zone, full card held by its corner while
            // roaming (mode/content are rendered from the dropTarget state).
            const g = cursorGhostRef.current;
            if (g) {
                g.style.transform = dropTargetRef.current
                    ? `translate(${ev.clientX + 12}px, ${ev.clientY + 14}px)`
                    : `translate(${ev.clientX - grabOffsetRef.current.x}px, ${ev.clientY - grabOffsetRef.current.y}px)`;
            }
        };

        const finish = (commit) => {
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onUp);
            window.removeEventListener('keydown', onKey, true);
            document.body.classList.remove('fbw-dragging');
            if (!active) return; // plain click — selection proceeds normally
            if (commit) {
                commitDrop();
            } else {
                dragIdRef.current = null;
                dragNodeRef.current = null;
                dropTargetRef.current = null;
                setDropTargetState(null);
                setDragNode(null);
                setDragId(null);
            }
        };

        const onUp = () => finish(true);
        const onKey = (ev) => { if (ev.key === 'Escape') { ev.stopPropagation(); finish(false); } };

        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
        window.addEventListener('keydown', onKey, true);
    }, [setDropAt, computeTarget, commitDrop]);

    /** A drag release also fires a click on the source — swallow that one. */
    const consumeClickSuppression = useCallback(() => {
        const s = suppressClickRef.current;
        suppressClickRef.current = false;
        return s;
    }, []);

    // ── Field operations ──

    const makeNewField = (type) => {
        if (type === 'container') {
            return { field_id: genId(), label: '', field_type: 'container', columns: 2, children: [] };
        }
        return {
            field_id: genId(), label: '', field_type: type, required: false,
            placeholder: '', options: [], map_to: null,
        };
    };

    const handleQuickAdd = (type) => {
        const newNode = makeNewField(type);
        if (selectedFieldId) {
            const found = findNode(fields, selectedFieldId);
            if (found && found.node.field_type === 'container') {
                const childCount = found.node.children?.length || 0;
                setFields(prev => insertNode(prev, newNode, childCount, selectedFieldId));
            } else {
                setFields(prev => [...prev, newNode]);
            }
        } else {
            setFields(prev => [...prev, newNode]);
        }
        setSelectedFieldId(newNode.field_id);
        setShowQuickAdd(false);
    };

    const removeField = (fieldId) => {
        setFields(prev => removeNode(prev, fieldId));
        if (selectedFieldId === fieldId) setSelectedFieldId(null);
    };

    const updateField = (fieldId, updates) => {
        setFields(prev => updateNode(prev, fieldId, updates));
    };

    const handleTemplateChoose = async (template) => {
        setShowTemplateGallery(false);
        if (template.id === 'blank') {
            setFields([]);
            return;
        }
        try {
            const res = await axios.get(`${API}/forms/templates`, { headers: getAuthHeader() });
            const templates = res.data || [];
            const tpl = templates.find(t => t.id === template.id);
            if (tpl && tpl.fields) {
                const newFields = tpl.fields.map(f => cloneFieldWithNewIds(f));
                setFields(newFields);
                if (tpl.settings) {
                    setForm(prev => ({ ...prev, settings: { ...prev.settings, ...tpl.settings } }));
                }
                toast.success(`Template "${template.name}" loaded — customize freely!`);
            }
        } catch {
            setFields([]);
        }
    };

    const updateSettings = (key, value) => setForm(prev => ({ ...prev, settings: { ...prev.settings, [key]: value } }));
    // Per-step styling: updateStyling edits the active step's styling dict (not form-level).
    // Each form page node has its own unique styling. "Sync Style to All Pages" copies
    // the active step's styling to all question steps.
    const activeStep = (activeStepIdx >= 0 && steps[activeStepIdx]) ? steps[activeStepIdx] : null;
    const activeStepStyling = activeStep?.styling || form?.styling || {};

    const updateStyling = (key, value) => {
        if (activeStepIdx < 0 || !steps[activeStepIdx]) return;
        setSteps(prev => prev.map((s, i) => {
            if (i !== activeStepIdx) return s;
            const currentStyling = s.styling || {};
            // For nested objects like padding, merge to avoid wholesale replacement
            if (key === 'padding' && typeof value === 'object') {
                return { ...s, styling: { ...currentStyling, padding: { ...(currentStyling.padding || {}), ...value } } };
            }
            return { ...s, styling: { ...currentStyling, [key]: value } };
        }));
        setIsDirty(true);
    };

    // Sync the active step's styling to all question steps
    const syncStyleToAllPages = () => {
        if (activeStepIdx < 0 || !steps[activeStepIdx]) {
            toast.error('No active form page to sync from');
            return;
        }
        const sourceStyling = steps[activeStepIdx].styling || {};
        setSteps(prev => prev.map(s =>
            s.kind === 'question' ? { ...s, styling: { ...sourceStyling } } : s
        ));
        setIsDirty(true);
        toast.success('Style synced to all form pages');
    };

    const flatFields = useMemo(() => flattenFields(fields), [fields]);
    const fieldsById = useMemo(() => {
        const m = {};
        flatFields.forEach(f => { if (f.field_id) m[f.field_id] = f; });
        return m;
    }, [flatFields]);
    const selectedField = selectedFieldId ? findNode(fields, selectedFieldId)?.node : null;
    const selectedContainer = selectedField?.field_type === 'container' ? selectedField : null;

    if (loading) return <div className="flex flex-1 items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-stone-400" /></div>;
    if (!form) return null;

    // Forwarders and Redirects have dedicated workflow nodes — no tabs here.
    const TABS = [
        { key: 'form', label: 'Form', icon: Eye },
        { key: 'settings', label: 'Settings', icon: Settings2 },
        { key: 'styling', label: 'Styling', icon: Palette },
        { key: 'embed', label: 'Embed', icon: Code },
    ];

    // Context bag handed down to every CanvasElement/CanvasStack.
    const canvasCtx = {
        selectedFieldId,
        onSelect: setSelectedFieldId,
        onDelete: removeField,
        dragId, dragNode, dropTarget,
        onPointerDragStart: startPointerDrag,
        consumeClickSuppression,
        answers: previewAnswers, setAnswers: setPreviewAnswers, errors: previewErrors,
        fieldsById,
        device,
    };

    const startResize = (e) => {
        e.preventDefault();
        const startX = e.clientX;
        const startW = paneWidth;
        const onMove = (ev) => {
            const newW = Math.min(700, Math.max(300, startW + ev.clientX - startX));
            setPaneWidth(newW);
        };
        const onUp = () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
    };

    return (
        <div className="flex-1 min-h-0 flex flex-col bg-stone-50">
            <style>{`
                /* ── Top Bar ── */
                .fb-topbar { background:#FFFFFF; border-bottom:1px solid #E8E5DF; padding:10px 16px; }
                .fb-back-btn { width:32px; height:32px; border-radius:6px; color:#57534E; display:flex; align-items:center; justify-content:center; transition:background .15s; }
                .fb-back-btn:hover { background:#F1F0EB; }
                .fb-form-name { font-size:14px; font-weight:600; color:#1C1917; background:transparent; border:none; outline:none; }
                .fb-save-status { font-size:13px; color:#57534E; }
                .fb-icon-btn { width:28px; height:28px; border-radius:6px; display:flex; align-items:center; justify-content:center; color:#57534E; transition:background .15s; }
                .fb-icon-btn:hover { background:#F1F0EB; }
                .fb-icon-btn:disabled { opacity:.35; }
                .fb-save-btn { background:#1C1917; color:#F7F6F2; padding:6px 12px; border-radius:6px; font-size:13px; font-weight:500; transition:background .15s; }
                .fb-save-btn:hover { background:#292524; }
                .fb-save-btn:disabled { opacity:.5; }

                /* ── Left Panel ── */
                .fb-left { background:#FFFFFF; border-right:1px solid #E8E5DF; }
                .fb-section-a { border-bottom:1px solid #F1F0EB; padding:16px; }
                .fb-section-title { font-size:14px; font-weight:600; color:#57534E; }
                .fb-section-count { font-size:12px; color:#A29D96; }
                .fb-section-c { flex:1; padding:12px 16px; overflow-y:auto; }

                .fb-resize-handle { width:1px; background:#E8E5DF; cursor:ew-resize; transition:background .15s, width .15s; }
                .fb-resize-handle:hover { background:#6366F1; width:2px; }

                /* ── Right Panel ── */
                .fb-tab-bar { background:#FFFFFF; border-bottom:1px solid #E8E5DF; padding:0 16px; }
                .fb-tab { padding:10px 12px; font-size:13px; font-weight:500; border-bottom:2px solid transparent; color:#A29D96; transition:color .15s, border-color .15s; display:flex; align-items:center; gap:6px; }
                .fb-tab:hover { color:#78716C; }
                .fb-tab.active { border-bottom:2px solid #1C1917; color:#1C1917; }
                .fb-tab-content { flex:1; overflow-y:auto; padding:24px; }

                .fb-device-btn { padding:4px; border-radius:6px; color:#A29D96; transition:background .15s, color .15s; }
                .fb-device-btn.active { background:#F1F0EB; color:#1C1917; }

                /* ── Canvas Card ── */
                .fb-canvas-card { max-width:100%; background:#FFFFFF; border:1px solid #E8E5DF; border-radius:12px; padding:24px; box-shadow:0 1px 2px rgba(0,0,0,.05); min-height:400px; }
                .fb-canvas-title { font-size:18px; font-weight:600; color:#1C1917; }
                .fb-canvas-desc { font-size:14px; color:#78716C; }
                .container-grid { display:grid; gap:0.75rem; grid-template-columns:repeat(var(--container-cols), minmax(0, 1fr)); }
                /* @media column collapse removed — CanvasStack now collapses
                   columns in JS based on the device toggle, not the browser
                   viewport. The old @media rule checked the browser window
                   width, not the canvas card width, so selecting "phone" on a
                   desktop browser kept 2 columns in a 375px canvas. */

                /* ── WYSIWYG canvas elements ── */
                /* touch-action:none lets the pointer engine own touch drags too. */
                .fbw-el { position:relative; border:1px solid transparent; border-radius:8px; padding:6px 8px; transition:border-color .12s, box-shadow .12s; cursor:grab; touch-action:none; }
                body.fbw-dragging, body.fbw-dragging * { cursor:grabbing !important; user-select:none !important; }
                .fbw-el:active { cursor:grabbing; }
                .fbw-el:hover { border-color:#D6D3D1; }
                .fbw-el.selected { border-color:#6366F1; box-shadow:0 0 0 2px rgba(99,102,241,0.15); }
                /* The drag source collapses in place — the node must stay
                   mounted (Chrome aborts a native drag when the source node
                   is removed or reparented mid-drag). */
                .fbw-el.drag-source { height:0 !important; min-height:0 !important; padding:0 !important; margin:0 !important; border:none !important; opacity:0; overflow:hidden; }
                /* In-form ghost placeholder at the prospective drop position.
                   The drag engine keeps the target stable geometrically while
                   the pointer is inside it. Height-capped: swapping
                   strip↔ghost shifts layout by ~40px instead of a full
                   element height, so drop targets don't jump while aiming. */
                .fbw-ghost { opacity:.55; border:1px dashed #6366F1; border-radius:8px; background:rgba(99,102,241,0.06); padding:6px 8px; max-height:56px; overflow:hidden; pointer-events:none; }
                /* "Drop here" strip between elements while a drag is active;
                   hovering one swaps it for the ghost placeholder. */
                .fbw-drop-gap { height:16px; border:1.5px dashed #C7D2FE; border-radius:6px; background:rgba(99,102,241,0.03); display:flex; align-items:center; justify-content:center; }
                .fbw-drop-gap span { font-size:8px; font-weight:600; letter-spacing:.08em; text-transform:uppercase; color:#A5B4FC; line-height:1; pointer-events:none; }
                /* Cursor-following ghost (positioned via transform from JS,
                   portaled to <body>). Capped so tall elements become a
                   compact card in hand instead of a huge sheet. */
                .fbw-cursor-ghost { position:fixed; top:0; left:0; z-index:1000; pointer-events:none; opacity:.8; background:#FFFFFF; border:1px solid #E8E5DF; border-radius:8px; padding:6px 8px; box-shadow:0 8px 24px rgba(0,0,0,.14); max-width:480px; max-height:140px; overflow:hidden; will-change:transform; }
                /* Inside a drop zone the in-form placeholder previews the
                   result — the cursor keeps a small chip so the hand never
                   looks empty. */
                .fbw-cursor-ghost.mini { max-width:220px; padding:4px 10px; border-radius:999px; background:#1C1917; border:none; box-shadow:0 4px 12px rgba(0,0,0,.25); opacity:.9; }
                .fbw-mini-ghost { display:flex; align-items:center; gap:6px; font-size:11px; font-weight:500; color:#F7F6F2; white-space:nowrap; }
                .fbw-mini-ghost-label { overflow:hidden; text-overflow:ellipsis; max-width:170px; }
                .fbw-tools { position:absolute; top:2px; right:2px; display:none; align-items:center; gap:2px; background:#FFFFFF; border:1px solid #E8E5DF; border-radius:6px; padding:1px 2px; box-shadow:0 1px 3px rgba(0,0,0,.08); z-index:5; }
                .fbw-el:hover > .fbw-tools, .fbw-el.selected > .fbw-tools { display:flex; }
                .fbw-del { color:#A29D96; padding:2px; border-radius:4px; display:flex; }
                .fbw-del:hover { color:#C2363F; background:#FBE7E8; }
                .fbw-hidden-badge { position:absolute; top:2px; left:8px; font-size:9px; color:#B45309; background:#FCEFDD; padding:1px 5px; border-radius:4px; z-index:4; }
                .fbw-derived-badge { position:absolute; top:2px; left:8px; font-size:9px; color:#4338CA; background:#E0E7FF; padding:1px 5px; border-radius:4px; z-index:4; }
                .fbw-badge { font-size:9px; color:#A29D96; background:#F1F0EB; padding:1px 4px; border-radius:4px; }
                .fbw-container-body { border:1px dashed #E8E5DF; border-radius:8px; padding:8px; }
                .fbw-container-body.droppable { border-color:#A5B4FC; background:rgba(99,102,241,0.04); }
                .fbw-add-btn { width:100%; border:1px dashed #D6D3D1; color:#78716C; border-radius:8px; padding:10px; font-size:13px; display:flex; align-items:center; justify-content:center; gap:6px; transition:border-color .15s, color .15s, background .15s; background:#FAFAF9; }
                .fbw-add-btn:hover { border-color:#6366F1; color:#6366F1; background:rgba(99,102,241,0.04); }
            `}</style>

            {/* Top bar */}
            <div className="fb-topbar flex items-center justify-between flex-shrink-0 z-10">
                <div className="flex items-center gap-3">
                    <button onClick={() => blockedNavigate(isNew ? '/forms' : `/forms/${formId}/workflow`)} className="fb-back-btn" title="Back to workflow">
                        <ChevronLeft className="w-4 h-4" />
                    </button>
                    <input value={(activeStepIdx >= 0 && steps[activeStepIdx]?.title) || form.name}
                        onChange={e => {
                            const val = e.target.value;
                            if (activeStepIdx >= 0 && steps[activeStepIdx]) {
                                setSteps(steps.map((s, i) => i === activeStepIdx ? { ...s, title: val } : s));
                            }
                            setForm({ ...form, name: val });
                        }}
                        className="fb-form-name"
                        style={{ width: 384 }}
                        placeholder={activeStepIdx >= 0 ? 'Node title...' : 'Internal form name...'} />
                    <span className="fb-save-status">
                        {saveStatus === 'saving' && 'Saving...'}
                        {saveStatus === 'saved' && '✓ Saved'}
                        {saveStatus === 'error' && '⚠ Save failed'}
                        {saveStatus === 'idle' && isDirty && 'Unsaved changes *'}
                    </span>
                </div>
                <div className="flex items-center gap-2">
                    <button onClick={undo} disabled={!canUndo} className="fb-icon-btn" title="Undo">
                        <Undo2 className="w-4 h-4" />
                    </button>
                    <button onClick={redo} disabled={!canRedo} className="fb-icon-btn" title="Redo">
                        <Redo2 className="w-4 h-4" />
                    </button>
                    <button onClick={handleManualSave} disabled={saving} className="fb-save-btn flex items-center gap-1.5">
                        {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                        Save
                    </button>
                </div>
            </div>

            {/* Main content */}
            <div className="flex-1 flex overflow-hidden">
                {/* Left: settings of the selected element only — elements are
                    added, selected, and arranged directly on the canvas. */}
                <div style={{ width: paneWidth }} className="fb-left overflow-hidden flex-shrink-0 relative flex flex-col">
                    <div className="fb-section-a flex-shrink-0">
                        <div className="flex items-center justify-between">
                            <h2 className="fb-section-title">Element Settings</h2>
                            <span className="fb-section-count">{countFields(fields)} elements</span>
                        </div>
                    </div>

                    <div className="fb-section-c">
                        <FieldInspector
                            field={selectedField}
                            onUpdate={updates => updateField(selectedFieldId, updates)}
                            customFields={customFields}
                            allFields={flatFields}
                            forwarders={forwarders}
                            steps={steps}
                        />
                    </div>

                    {/* Resize handle */}
                    <div className="fb-resize-handle absolute right-0 top-0 bottom-0" onMouseDown={startResize} />
                </div>

                {/* Right: Tabs */}
                <div className="flex-1 flex flex-col overflow-hidden">
                    {/* Tab bar */}
                    <div className="fb-tab-bar flex gap-1 items-center">
                        {TABS.map(tab => {
                            const Icon = tab.icon;
                            return (
                                <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                                    className={`fb-tab ${activeTab === tab.key ? 'active' : ''}`}>
                                    <Icon className="w-3.5 h-3.5" /> {tab.label}
                                </button>
                            );
                        })}
                        {activeTab === 'form' && (
                            <div className="ml-auto flex items-center gap-1">
                                <button onClick={() => setDevice('desktop')} className={`fb-device-btn ${device === 'desktop' ? 'active' : ''}`}><Monitor className="w-3.5 h-3.5" /></button>
                                <button onClick={() => setDevice('tablet')} className={`fb-device-btn ${device === 'tablet' ? 'active' : ''}`}><Tablet className="w-3.5 h-3.5" /></button>
                                <button onClick={() => setDevice('phone')} className={`fb-device-btn ${device === 'phone' ? 'active' : ''}`}><Smartphone className="w-3.5 h-3.5" /></button>
                            </div>
                        )}
                    </div>

                    {/* Tab content */}
                    <div className="fb-tab-content">
                        {activeTab === 'settings' && (
                            <div className="max-w-md space-y-4">
                                <div>
                                    <label className={LABEL}>Public Form Title</label>
                                    <input value={form.form_title || ''} onChange={e => setForm({ ...form, form_title: e.target.value })}
                                        className={INPUT} placeholder="Title shown to the public (defaults to form name if empty)" />
                                </div>
                                <label className="flex items-center gap-2 text-sm">
                                    <input type="checkbox" checked={form.show_form_title !== false}
                                        onChange={e => setForm({ ...form, show_form_title: e.target.checked })}
                                        className="rounded border-stone-300" />
                                    Show title on public form
                                </label>
                                <div>
                                    <label className={LABEL}>Description</label>
                                    <textarea value={form.description || ''} onChange={e => setForm({ ...form, description: e.target.value })}
                                        rows={2} className={INPUT} placeholder="Optional form description..." />
                                </div>
                                <div>
                                    <label className={LABEL}>Confirmation Message</label>
                                    <textarea value={form.settings?.confirmation_message || ''}
                                        onChange={e => updateSettings('confirmation_message', e.target.value)}
                                        rows={2} className={INPUT} />
                                </div>
                                <div>
                                    <label className={LABEL}>Notify Emails on Submission</label>
                                    <TagInput value={form.settings?.notify_emails || []}
                                        onChange={v => updateSettings('notify_emails', v)} placeholder="admin@company.com" />
                                </div>
                                <p className="text-[11px] text-stone-400 leading-relaxed">
                                    Redirects and data forwarding are configured on their dedicated
                                    workflow nodes. CRM mapping lives on each element's “Map to CRM
                                    Field” setting.
                                </p>
                            </div>
                        )}

                        {activeTab === 'styling' && (
                            <div className="max-w-md space-y-4">
                                {/* Per-step indicator */}
                                <div className="flex items-center gap-2 px-3 py-2 bg-indigo-50 border border-indigo-200 rounded-lg">
                                    <Info className="w-3.5 h-3.5 text-indigo-500 flex-shrink-0" />
                                    <p className="text-[11px] text-indigo-700">
                                        Styling is unique to this form page.
                                        Use "Sync Style to All Pages" to apply to all pages.
                                    </p>
                                </div>

                                {/* ── Typography ── */}
                                <div className="border-t border-stone-200 pt-4 space-y-4">
                                    <h4 className="text-sm font-semibold text-stone-700">Typography</h4>
                                    <div>
                                        <label className={LABEL}>Font Size (px)</label>
                                        <input type="number" min="10" max="32"
                                            value={activeStepStyling?.font_size ?? ''}
                                            onChange={e => updateStyling('font_size', e.target.value === '' ? null : parseInt(e.target.value))}
                                            className={INPUT}
                                            placeholder="Default (inherit)" />
                                    </div>
                                    <div>
                                        <label className={LABEL}>Font Family</label>
                                        <select
                                            value={activeStepStyling?.font_family || ''}
                                            onChange={e => updateStyling('font_family', e.target.value || null)}
                                            className={INPUT}>
                                            <option value="">Default (Manrope)</option>
                                            {FONT_OPTIONS.map(f => (
                                                <option key={f.value} value={f.value}>{f.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                {/* ── Colors ── */}
                                <div className="border-t border-stone-200 pt-4 space-y-4">
                                    <h4 className="text-sm font-semibold text-stone-700">Colors</h4>
                                    <div>
                                        <label className={LABEL}>Brand Color</label>
                                        <ColorField
                                            value={activeStepStyling?.brand_color || '#1c1917'}
                                            onChange={v => updateStyling('brand_color', v)}
                                            fallback="#1c1917"
                                            allowClear={false}
                                            swatchLabel="Brand Color"
                                        />
                                    </div>
                                    <div>
                                        <label className={LABEL}>Button Text</label>
                                        <input value={activeStepStyling?.button_text || 'Submit'}
                                            onChange={e => updateStyling('button_text', e.target.value)} className={INPUT} />
                                    </div>
                                    <p className="text-xs text-stone-400">
                                        "Powered by NurtureCRM" branding can be toggled in the
                                        workflow canvas Settings panel.
                                    </p>

                                    {/* Button color override */}
                                    <div>
                                        <label className={LABEL}>Button Color (optional)</label>
                                        <ColorField
                                            value={activeStepStyling?.button_color || null}
                                            onChange={v => updateStyling('button_color', v)}
                                            fallback={activeStepStyling?.brand_color || '#1c1917'}
                                            placeholder="Uses brand color if empty"
                                            clearLabel="Reset"
                                            swatchLabel="Button Color"
                                        />
                                    </div>
                                </div>

                                {/* ── Form container styling ── */}
                                <div className="border-t border-stone-200 pt-4 space-y-4">
                                    <h4 className="text-sm font-semibold text-stone-700">Form Container</h4>

                                    {/* Padding (per-side) */}
                                    <div>
                                        <label className={LABEL}>Padding (px)</label>
                                        <div className="grid grid-cols-4 gap-1.5">
                                            {['top', 'right', 'bottom', 'left'].map(side => (
                                                <div key={side}>
                                                    <input type="number" min={0} max={100}
                                                        value={activeStepStyling?.padding?.[side] ?? 0}
                                                        onChange={e => updateStyling('padding', {
                                                            [side]: e.target.value === '' ? 0 : Math.max(0, Math.min(100, parseInt(e.target.value, 10) || 0)),
                                                        })}
                                                        className="w-full px-1.5 py-1.5 text-xs text-center border border-stone-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/20"
                                                        placeholder="0" />
                                                    <p className="text-[9px] text-stone-400 text-center mt-0.5 capitalize">{side}</p>
                                                </div>
                                            ))}
                                        </div>
                                        <p className="text-[10px] text-stone-400 mt-1">Inner spacing between the form edge and its content. Especially useful for embeds where 0 padding can cut off input edges.</p>
                                    </div>

                                    {/* Background color */}
                                    <div>
                                        <label className={LABEL}>Background Color</label>
                                        <ColorField
                                            value={activeStepStyling?.background === 'transparent' ? null : (activeStepStyling?.background || null)}
                                            onChange={v => updateStyling('background', v)}
                                            allowFreeText
                                            allowClear={false}
                                            fallback="#ffffff"
                                            swatchLabel="Background Color"
                                            placeholder="transparent"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => updateStyling('background', 'transparent')}
                                            className="mt-1 text-xs text-indigo-600 hover:underline whitespace-nowrap">
                                                Reset to transparent
                                        </button>
                                        <p className="text-xs text-stone-500 mt-1">Use "transparent" to show the page background.</p>
                                    </div>

                                    {/* Border radius */}
                                    <div>
                                        <label className={LABEL}>Corner Radius (px)</label>
                                        <input type="number" min="0" max="48"
                                            value={activeStepStyling?.border_radius ?? 12}
                                            onChange={e => updateStyling('border_radius', parseInt(e.target.value) || 0)}
                                            className={INPUT} />
                                    </div>

                                    {/* Border width + color */}
                                    <div>
                                        <label className={LABEL}>Border Width (px)</label>
                                        <input type="number" min="0" max="10"
                                            value={activeStepStyling?.border_width ?? 1}
                                            onChange={e => updateStyling('border_width', parseInt(e.target.value) || 0)}
                                            className={INPUT} />
                                        <p className="text-xs text-stone-500 mt-1">Set to 0 for no border.</p>
                                    </div>
                                    {activeStepStyling?.border_width > 0 && (
                                        <div>
                                            <label className={LABEL}>Border Color</label>
                                            <ColorField
                                                value={activeStepStyling?.border_color || null}
                                                onChange={v => updateStyling('border_color', v)}
                                                fallback="#E8E5DF"
                                                swatchLabel="Border Color"
                                                clearLabel="Reset"
                                                inputClass={INPUT}
                                            />
                                        </div>
                                    )}
                                </div>

                                {/* ── Sync to all pages ── */}
                                <div className="border-t border-stone-200 pt-4">
                                    <button
                                        onClick={syncStyleToAllPages}
                                        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-indigo-200 bg-indigo-50 text-indigo-700 text-sm font-medium hover:bg-indigo-100 transition-colors">
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                                            stroke="currentColor" strokeWidth="2"
                                            strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M21 12a9 9 0 0 1-9 9m9-9a9 9 0 0 0-9-9m9 9H3 m9 9a9 9 0 0 1-9-9m9 9c-2.5-2.5-4-5.5-4-9s1.5-6.5 4-9 m0 18c2.5-2.5 4-5.5 4-9s-1.5-6.5-4-9" />
                                        </svg>
                                        Sync Style to All Pages
                                    </button>
                                    <p className="text-[10px] text-stone-400 mt-1.5 text-center">
                                        Copies this page's styling (colors, fonts, borders, padding) to all form pages.
                                    </p>
                                </div>
                            </div>
                        )}

                        {activeTab === 'form' && (
                            <div className="mx-auto" style={{ maxWidth: DEVICE_WIDTHS[device] }}>
                                {/* ── WYSIWYG canvas: the live form, editable in place ── */}
                                <div className="fb-canvas-card"
                                    style={{
                                        background: !activeStepStyling?.background ? undefined : activeStepStyling.background,
                                        borderRadius: (activeStepStyling?.border_radius ?? 12) + 'px',
                                        borderWidth: (activeStepStyling?.border_width ?? 1) > 0 ? (activeStepStyling?.border_width ?? 1) + 'px' : '0',
                                        borderColor: activeStepStyling?.border_color || '#E8E5DF',
                                        ...paddingStyle(activeStepStyling?.padding),
                                        ...(activeStepStyling?.font_size ? { fontSize: `${activeStepStyling.font_size}px` } : {}),
                                        ...(activeStepStyling?.font_family ? { fontFamily: activeStepStyling.font_family } : {}),
                                    }}
                                    onClick={() => { if (consumeClickSuppression()) return; setSelectedFieldId(null); }}>
                                    {form.description && <p className="fb-canvas-desc mb-4">{form.description}</p>}

                                    {fields.length === 0 && (
                                        <div className="text-center py-10 text-stone-400 border-2 border-dashed border-stone-200 rounded-lg mb-4">
                                            <p className="text-sm">No elements yet</p>
                                            <p className="text-xs mt-1">
                                                Add your first element below, or{' '}
                                                <button onClick={(e) => { e.stopPropagation(); setShowTemplateGallery(true); }}
                                                    className="text-indigo-600 hover:underline">choose a template</button>
                                            </p>
                                        </div>
                                    )}

                                    <CanvasStack nodes={fields} parentId={null} ctx={canvasCtx} />

                                    {/* Add Element — lives inside the canvas */}
                                    <div className="relative mt-4" onClick={(e) => e.stopPropagation()}>
                                        <button onClick={() => setShowQuickAdd(!showQuickAdd)} className="fbw-add-btn">
                                            <Plus className="w-4 h-4" />
                                            Add Element{selectedContainer ? ` to “${selectedContainer.label || 'Container'}”` : ''}
                                        </button>
                                        {showQuickAdd && (
                                            <>
                                                <div className="fixed inset-0 z-10" onClick={() => setShowQuickAdd(false)} />
                                                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-stone-200 rounded-lg shadow-lg z-30 max-h-64 overflow-y-auto">
                                                    {FIELD_TYPES.map(t => (
                                                        <button key={t.value} onClick={() => handleQuickAdd(t.value)}
                                                            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-stone-700 hover:bg-stone-50 transition-colors text-left">
                                                            <span>{t.icon}</span>
                                                            <span>{t.label}</span>
                                                            {selectedContainer && (
                                                                <span className="ml-auto text-[9px] text-indigo-500">→ {selectedContainer.label || 'Container'}</span>
                                                            )}
                                                        </button>
                                                    ))}
                                                </div>
                                            </>
                                        )}
                                    </div>

                                    <button onClick={(e) => {
                                        e.stopPropagation();
                                        const errors = {};
                                        flatFields.filter(f => f.required && !f.hidden && evaluateShowWhen(f, previewAnswers, fieldsById)).forEach(f => {
                                            if (!previewAnswers[f.field_id]) errors[f.field_id] = 'Required';
                                        });
                                        setPreviewErrors(errors);
                                        if (Object.keys(errors).length === 0) {
                                            toast.success('Preview submission successful! (no data sent)');
                                        }
                                    }} className="mt-4 w-full py-2.5 rounded-lg text-white font-medium text-sm"
                                        style={{ backgroundColor: activeStepStyling?.brand_color || '#1c1917' }}>
                                        {activeStepStyling?.button_text || 'Submit'}
                                    </button>
                                    {form?.styling?.show_branding !== false && (
                                        <p className="text-center text-[10px] text-stone-400 mt-3">Powered by NurtureCRM</p>
                                    )}
                                </div>
                            </div>
                        )}

                        {activeTab === 'embed' && (
                            <div className="max-w-lg space-y-4">
                                {form.slug ? (() => {
                                    const snippet = buildFormEmbedSnippet({ slug: form.slug, device: embedDevice });
                                    return (
                                    <>
                                        <div>
                                            <label className={LABEL}>Device Mode</label>
                                            <select
                                                value={embedDevice}
                                                onChange={e => setEmbedDevice(e.target.value)}
                                                className={INPUT}
                                            >
                                                {DEVICE_VALUES.map(d => (
                                                    <option key={d} value={d}>{DEVICE_LABELS[d]}</option>
                                                ))}
                                            </select>
                                            <p className="text-xs text-stone-500 mt-1">
                                                Constrains the form width for the target device.
                                            </p>
                                            <div className="mt-2 flex gap-2 rounded-md border border-blue-100 bg-blue-50 px-3 py-2">
                                                <Info className="w-3.5 h-3.5 text-blue-500 mt-0.5 shrink-0" />
                                                <p className="text-xs text-blue-700 leading-relaxed">
                                                    The <code className="font-mono">?device=</code> query parameter controls the form's maximum
                                                    width inside the iframe. <strong>Phone</strong> → 375px, <strong>Tablet</strong> → 768px,
                                                    <strong> Desktop</strong> → no limit. Both snippets impose zero fixed sizing — the iframe
                                                    height auto-adapts to the form content, and the width defaults to 100% of your container
                                                    (overridable via CSS). No scrollbar, ever.
                                                </p>
                                            </div>
                                        </div>
                                        <div>
                                            <label className={LABEL}>Public URL</label>
                                            <div className="flex items-center gap-2">
                                                <code className="flex-1 text-xs bg-stone-50 px-3 py-2 rounded border border-stone-200">
                                                    {snippet.publicUrl}
                                                </code>
                                                <Button variant="outline" size="sm" onClick={() => {
                                                    navigator.clipboard.writeText(snippet.publicUrl);
                                                    toast.success('URL copied');
                                                }}><Copy className="w-3.5 h-3.5" /></Button>
                                            </div>
                                        </div>
                                        <div>
                                            <label className={LABEL}>iframe Embed Code</label>
                                            <textarea readOnly rows={3} className="w-full text-xs font-mono bg-stone-50 px-3 py-2 rounded border border-stone-200"
                                                value={snippet.iframeCode} />
                                        </div>
                                        <div>
                                            <label className={LABEL}>Script Tag (auto-resize)</label>
                                            <textarea readOnly rows={5} className="w-full text-xs font-mono bg-stone-50 px-3 py-2 rounded border border-stone-200"
                                                value={snippet.scriptCode} />
                                        </div>
                                    </>
                                    );
                                })() : (
                                    <p className="text-sm text-stone-400">Save the form first to get embed codes.</p>
                                )}
                            </div>
                        )}

                    </div>
                </div>
            </div>

            {/* Cursor ghost — follows the pointer while dragging. Full card
                while roaming; inside a drop zone (where the in-form
                placeholder previews the result) it shrinks to a small chip
                so the hand never looks empty. Portaled to document.body so
                position:fixed is guaranteed viewport-relative — a
                transformed/filtered ancestor anywhere in the shell would
                otherwise become its containing block and skew it away from
                the cursor. Positioned from the very first frame. */}
            {dragId && dragNode && createPortal(
                <div ref={cursorGhostRef} className={`fbw-cursor-ghost ${dropTarget ? 'mini' : ''}`}
                    style={{
                        width: dropTarget ? undefined : dragSizeRef.current.width,
                        transform: dropTarget
                            ? `translate(${ghostPosRef.current.x + 12}px, ${ghostPosRef.current.y + 14}px)`
                            : `translate(${ghostPosRef.current.x - grabOffsetRef.current.x}px, ${ghostPosRef.current.y - grabOffsetRef.current.y}px)`,
                    }}>
                    {dropTarget ? (
                        <span className="fbw-mini-ghost">
                            <span>{TYPE_ICONS[dragNode.field_type] || '❓'}</span>
                            <span className="fbw-mini-ghost-label">
                                {dragNode.label || FIELD_TYPES.find(t => t.value === dragNode.field_type)?.label || 'Element'}
                            </span>
                        </span>
                    ) : (
                        <GhostBody node={dragNode} />
                    )}
                </div>,
                document.body
            )}

            {/* Template Gallery Modal */}
            <FormTemplateGallery open={showTemplateGallery} onClose={() => setShowTemplateGallery(false)} onChoose={handleTemplateChoose} />

            {/* Navigation Confirm Modal */}
            {showNavConfirm && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full p-6">
                        <h3 className="text-lg font-bold text-stone-900 mb-2">Unsaved Changes</h3>
                        <p className="text-sm text-stone-500 mb-4">You have unsaved changes. Are you sure you want to leave?</p>
                        <div className="flex justify-end gap-2">
                            <button onClick={cancelLeave} className="px-4 py-2 text-sm border border-stone-200 rounded-lg hover:bg-stone-50">Stay</button>
                            <button onClick={confirmLeave} className="px-4 py-2 text-sm bg-red-500 text-white rounded-lg hover:bg-red-600">Leave</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
